import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Book, ChevronDown } from "lucide-react";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "../components/ui/breadcrumb";
import { Button } from "../components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Link } from 'react-router-dom';

const CookiesPolicy = () => {
  const [isEssentialOpen, setIsEssentialOpen] = React.useState(true);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <Link to="/" className="text-blue-600 hover:underline text-sm">← Back to WANTTD</Link>
          </div>
          
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="#">Legal</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink>Cookies Policy</BreadcrumbLink>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          <Card className="shadow-sm border border-gray-200">
            <CardHeader className="pb-2 border-b">
              <div className="flex items-center gap-3 mb-2">
                <Book className="h-6 w-6 text-gray-700" />
                <CardTitle className="text-2xl font-bold text-gray-800">Cookies Policy</CardTitle>
              </div>
              <p className="text-sm text-gray-500">Last Updated: April 2025</p>
            </CardHeader>
            
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                  <h2 className="text-lg font-medium mb-3">What Are Cookies</h2>
                  <p className="text-gray-600 text-sm mb-4">
                    Cookies are small text files that are stored on your computer or mobile device when you visit a website. 
                    They allow the website to recognize your device and remember if you've been to the website before.
                  </p>
                  
                  <p className="text-gray-600 text-sm">
                    WANTTD uses cookies and similar technologies to enhance your experience on our platform, analyze how our 
                    services are used, personalize content, and assist in our marketing efforts.
                  </p>
                </div>
                
                <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm">
                  <h2 className="text-lg font-medium mb-3">Types of Cookies We Use</h2>
                  <p className="text-gray-600 text-sm mb-4">We use the following types of cookies:</p>
                  
                  <div className="space-y-3">
                    <Collapsible
                      open={isEssentialOpen}
                      onOpenChange={setIsEssentialOpen}
                      className="border rounded-md overflow-hidden"
                    >
                      <CollapsibleTrigger asChild>
                        <Button 
                          variant="ghost" 
                          className="flex w-full justify-between p-4 font-medium text-left"
                        >
                          Essential Cookies
                          <ChevronDown className={`h-4 w-4 transition-transform ${isEssentialOpen ? 'rotate-180' : ''}`} />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="p-4 pt-0 text-sm text-gray-600 border-t">
                        <p>
                          These cookies are necessary for the website to function properly. They enable core functionality such as 
                          security, network management, and account access. You may disable these by changing your browser settings, 
                          but this may affect how the website functions.
                        </p>
                      </CollapsibleContent>
                    </Collapsible>
                    
                    <Collapsible
                      open={isAnalyticsOpen}
                      onOpenChange={setIsAnalyticsOpen}
                      className="border rounded-md overflow-hidden"
                    >
                      <CollapsibleTrigger asChild>
                        <Button 
                          variant="ghost" 
                          className="flex w-full justify-between p-4 font-medium text-left"
                        >
                          Performance and Analytics Cookies
                          <ChevronDown className={`h-4 w-4 transition-transform ${isAnalyticsOpen ? 'rotate-180' : ''}`} />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="p-4 pt-0 text-sm text-gray-600 border-t">
                        <p>
                          These cookies help us understand how visitors interact with our website by collecting and reporting information 
                          anonymously. They help us improve the functionality of our site.
                        </p>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                </div>
                
                <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-blue-700">Want to learn more about how to manage cookies?</p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-blue-200 text-blue-600 hover:bg-blue-100"
                        >
                          Cookie Preferences
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Manage your cookie preferences</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CookiesPolicy;
